import {
  ForgeButton,
  ForgeDialog,
  ForgeExpansionPanel,
  ForgeIcon,
  ForgeIconButton,
  ForgeList,
  ForgeListItem,
  ForgeOpenIcon,
  ForgeScaffold,
  ForgeTextField,
  ForgeToolbar,
  ForgeTooltip,
} from '@tylertech/forge-react';
import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import './index.scss';
import { useEscapeKey } from 'common/visualizations/views/agGridReact/customHooks/useEscapeKey';
import { IDialogBeforeCloseEventData } from '@tylertech/forge';
import EventBus from 'common/visualizations/views/agGridReact/helpers/EventBus';
import { I18n } from 'common/i18n';
import { Vif } from 'common/visualizations/vif';
import { findHierarchyInfo } from 'common/visualizations/helpers/AgTableVifHelpers';
import { getGroupingColumnNames } from '../../panes/DataPane/TableHierarchyGroupings/components/TableHierarchyGroupingHeader';
import get from 'lodash/get';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import SubtotalsSection from './SubtotalsSection';
import { useDispatch } from 'react-redux';
import { updateCustomFormulas } from 'common/authoring_workflow/actions';
import { filterEmptyFormulas, processFormulas } from './helpers';

interface AdvancedTotalingModalProps {
  isTableAdvancedTotalingModalOpen: boolean;
  advancedTotalingModalTitle: string;
  advancedTotalingModalHierarchyId: string;
  vif: Vif;
}

const scope = 'shared.components.advanced_totaling.modal';

const AdvancedTotalingModal: React.FC<AdvancedTotalingModalProps> = ({
  isTableAdvancedTotalingModalOpen,
  advancedTotalingModalTitle,
  advancedTotalingModalHierarchyId,
  vif,
}: AdvancedTotalingModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  // Local state for custom formulas (not saved to VIF until Apply is clicked)
  const [customFormulas, setCustomFormulas] = useState<{ [columnName: string]: string }>({});

  const selectedColumnGroupings = useMemo(() => {
    const { hierarchies, hierarchyIndex } = findHierarchyInfo(vif, advancedTotalingModalHierarchyId);
    const columnConfigs = filter(
      hierarchies[hierarchyIndex]?.columnConfigurations,
      (config) => config.isGrouping,
    );
    return columnConfigs;
  }, [advancedTotalingModalHierarchyId, vif]);

  // Load existing custom formulas from VIF into local state when modal opens
  const existingFormulas = useMemo(() => {
    if (
      !advancedTotalingModalHierarchyId ||
      !isTableAdvancedTotalingModalOpen ||
      !selectedColumnGroupings.length
    ) {
      return {};
    }

    const formulas: { [columnName: string]: string } = {};
    selectedColumnGroupings.forEach((config) => {
      if (config.customFormula) {
        formulas[config.columnName] = config.customFormula;
      }
    });

    return formulas;
  }, [advancedTotalingModalHierarchyId, isTableAdvancedTotalingModalOpen, selectedColumnGroupings]);

  useEffect(() => {
    if (isTableAdvancedTotalingModalOpen) {
      setCustomFormulas(existingFormulas);
    }
  }, [isTableAdvancedTotalingModalOpen, existingFormulas]);

  const hasChanges = useMemo(() => {
    return !isEqual(processFormulas(customFormulas), processFormulas(existingFormulas));
  }, [customFormulas, existingFormulas]);

  const hasFormulas = useMemo(() => {
    return filterEmptyFormulas(customFormulas).length > 0;
  }, [customFormulas]);

  const handleApplyFormulas = () => {
    const { hierarchyIndex } = findHierarchyInfo(vif, advancedTotalingModalHierarchyId);

    // Filter out empty formulas before saving
    const filteredFormulas = Object.fromEntries(filterEmptyFormulas(customFormulas));

    // Dispatch the action to save formulas to VIF
    dispatch(updateCustomFormulas(hierarchyIndex, filteredFormulas));

    // Close the modal after saving
    hideModal();
  };

  const handleClearAllFormulas = () => {
    // Clear all formula values by setting them to empty strings
    const clearedFormulas: { [columnName: string]: string } = {};
    selectedColumnGroupings.forEach((config) => {
      clearedFormulas[config.columnName] = '';
    });
    setCustomFormulas(clearedFormulas);
  };

  const hideModal = useCallback(() => {
    EventBus.dispatch('advancedTotaling', {
      advancedTotalingModalTitle: '',
      isTableAdvancedTotalingModalOpen: false,
      advancedTotalingModalHierarchyId: undefined,
    });
  }, []);

  useEscapeKey({
    onKeyup: hideModal,
    enabled: isTableAdvancedTotalingModalOpen,
    eventTarget: modalRef.current || document,
    restoreFocusTarget: '.authoring-modal',
  });

  const columns = useMemo(() => get(vif, 'series[0].dataSource.dimension.columns', []), [vif]);
  const columnFormats = useMemo(() => get(vif, 'series[0].formatting.columnFormat', {}), [vif]);

  const modalTitle = useMemo(() => {
    return `${I18n.t('title', { scope })} - ${advancedTotalingModalTitle}`;
  }, [advancedTotalingModalTitle]);

  const groupingColumnNames = useMemo(() => {
    return getGroupingColumnNames(selectedColumnGroupings, columns, columnFormats);
  }, [columns, selectedColumnGroupings, columnFormats]);

  const renderBody = useMemo(() => {
    if (isEmpty(groupingColumnNames) || isEmpty(selectedColumnGroupings)) {
      return <></>;
    }

    const totalGroupColumnKey = selectedColumnGroupings[0].columnName;
    const totalGroup = groupingColumnNames[0];
    return (
      <>
        <div>
          <span className="forge-typography--body2 group-category">{I18n.t('totals', { scope })}</span>
        </div>
        <ForgeList>
          <ForgeExpansionPanel>
            <ForgeListItem slot={'header'} className={'expansion-header'} key={0}>
              <span className="forge-typography--body1" data-testid="totals-row">
                {totalGroup}
              </span>
              <ForgeOpenIcon slot={'trailing'}></ForgeOpenIcon>
            </ForgeListItem>
            <div>
              <ForgeTextField>
                <label className="forge-typography--label2">{I18n.t('enter_formula', { scope })}</label>
                <textarea
                  data-testid="totals-formula"
                  value={customFormulas[totalGroupColumnKey] || ''}
                  onChange={(e) =>
                    setCustomFormulas((prev) => ({ ...prev, [totalGroupColumnKey]: e.target.value }))
                  }
                ></textarea>
              </ForgeTextField>
            </div>
          </ForgeExpansionPanel>
        </ForgeList>
        <SubtotalsSection
          columnGroups={selectedColumnGroupings}
          columnNames={groupingColumnNames}
          subtotalFormulas={customFormulas}
          onSubtotalFormulaChange={(columnName: string, formula: string) => {
            setCustomFormulas((prev) => ({ ...prev, [columnName]: formula }));
          }}
        />
      </>
    );
  }, [selectedColumnGroupings, groupingColumnNames, customFormulas]);

  return (
    <ForgeDialog
      on-forge-dialog-before-close={(e: CustomEvent<IDialogBeforeCloseEventData>) => {
        if (e.detail.reason === 'backdrop') {
          e.preventDefault();
        }
      }}
      on-forge-dialog-close={hideModal}
      open={isTableAdvancedTotalingModalOpen}
      persistent={true}
      ref={modalRef}
      className="advanced-totaling-dialog"
      data-testid="advanced-totaling-dialog"
    >
      <div className={'advanced-totaling-modal'} data-testid="advanced-totaling-modal">
        <ForgeScaffold>
          <ForgeToolbar slot={'header'}>
            <div slot="start" className="modal-title-container">
              <ForgeTooltip placement={'top'} data-testid="advanced-totaling-modal-title-tooltip">
                {modalTitle}
              </ForgeTooltip>
              <h1 className="forge-typography--heading4" data-testid="advanced-totaling-modal-title">
                {modalTitle}
              </h1>
            </div>
            <ForgeIconButton slot={'end'} onClick={hideModal} data-testid="close-btn">
              <ForgeIcon name={'close'}></ForgeIcon>
            </ForgeIconButton>
          </ForgeToolbar>
          <div
            className="advanced-totaling-modal-body"
            data-testid="advanced-totaling-modal-body"
            slot="body"
          >
            {renderBody}
          </div>
          <ForgeToolbar slot={'footer'}>
            <ForgeButton
              slot={'start'}
              onClick={handleClearAllFormulas}
              disabled={!hasFormulas}
              data-testid="clear-all-formulas-btn"
            >
              {I18n.t('clear_all_formulas', { scope })}
            </ForgeButton>
            <ForgeButton
              slot={'end'}
              variant={'raised'}
              data-testid="apply-btn"
              onClick={handleApplyFormulas}
              disabled={!hasChanges}
            >
              {I18n.t('apply', { scope })}
            </ForgeButton>
          </ForgeToolbar>
        </ForgeScaffold>
      </div>
    </ForgeDialog>
  );
};

export default AdvancedTotalingModal;
